/**
 * Os três acervos de onde os livros vêm: Open Library, Projeto Gutenberg
 * (pelo Gutendex) e Internet Archive — este último também com os audiolivros
 * do LibriVox, que moram no mesmo acervo.
 *
 * Transcrito de `open_library_service.dart`, `gutenberg_service.dart` e
 * `internet_archive_service.dart`. Nenhum dos três pede chave: são endereços
 * públicos, e por isso a busca acontece direto do navegador, sem passar pela
 * nossa API. O texto do livro é a exceção — ver `montarCandidatos` e o
 * `POST /books/text` do servidor.
 */

import { ligacao } from '../nucleo/ligacao';
import { capaPorId, derivarFonte, derivarRotuloDaFonte, livroNovo, type Livro } from './livro';
import { resultadoNovo, type ResultadoDeLivro } from './resultado';
import { audiolivroNovo, type Audiolivro, type Faixa } from './audiolivro';

/** O mesmo prazo do resto do app. O Flutter não punha nenhum aqui. */
const PRAZO = 15_000;

/**
 * Uma chamada de catálogo. Devolve `null` em qualquer tropeço — busca de
 * acervo é linha de vitrine: uma fonte fora do ar não pode derrubar a tela
 * inteira, ela só some da mistura.
 */
export async function buscarJson<T>(url: string, prazo = PRAZO): Promise<T | null> {
  const controle = new AbortController();
  const corta = setTimeout(() => controle.abort(), prazo);
  try {
    const r = await fetch(url, { signal: controle.signal });
    if (!r.ok) return null;
    return (await r.json()) as T;
  } catch {
    return null;
  } finally {
    clearTimeout(corta);
  }
}

const texto = (v: unknown): string => (typeof v === 'string' ? v : '');
const inteiro = (v: unknown): number => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : 0;
};

/** `_asList`: o acervo manda ora um texto, ora uma lista deles. */
function comoLista(bruto: unknown): string[] {
  if (typeof bruto === 'string') return [bruto];
  if (Array.isArray(bruto)) return bruto.map((e) => String(e));
  return [];
}

/** `_first`. */
function primeiroTexto(bruto: unknown): string {
  if (typeof bruto === 'string') return bruto.trim();
  if (Array.isArray(bruto) && bruto.length > 0) return String(bruto[0]).trim();
  return '';
}

/**
 * `_extractDescription`: texto, lista de parágrafos ou `{value}`.
 *
 * A tira de HTML é nossa. Lá o `semHtml` só é chamado na descrição do
 * audiolivro, e a do livro chega inteira na tela: de vinte e três itens de
 * texto do Archive, dez traziam marcação, e ela aparecia escrita — "Obra
 * crítica de Araripe Júnior&lt;br /&gt;" no caso curto, um parágrafo de
 * `class` e `style` do Word no caso longo. O Open Library também tem os seus.
 * Como o único uso dessa descrição é ser lida por gente, a marcação sai aqui,
 * e não em cada lugar que pergunta.
 */
export function descricaoDe(bruto: unknown): string {
  if (typeof bruto === 'string') return semHtml(bruto);
  if (Array.isArray(bruto)) return semHtml(bruto.map((e) => String(e)).join('\n\n'));
  if (bruto && typeof bruto === 'object') {
    const v = (bruto as Record<string, unknown>).value;
    if (typeof v === 'string') return semHtml(v);
  }
  return '';
}

// ─────────────────────────────────────────────────────────── Open Library ──

const BUSCA_OL = 'https://openlibrary.org/search.json';

interface DocOL {
  key?: string;
  title?: string;
  author_name?: unknown;
  cover_i?: unknown;
  first_publish_year?: unknown;
  ia?: unknown;
  subject?: unknown;
}

/** `_mapDocToBookItem`. */
function livroDeDocOL(doc: DocOL): Livro {
  const obra = texto(doc.key);
  const autores = comoLista(doc.author_name);
  const iaIds = comoLista(doc.ia);
  const iaId = iaIds.length > 0 ? iaIds[0] : '';
  const ano = inteiro(doc.first_publish_year);
  return livroNovo({
    obra,
    iaId,
    titulo: texto(doc.title) || 'Untitled',
    autor: autores.length > 0 ? autores[0] : '',
    capa: capaPorId(inteiro(doc.cover_i) || null, 'L'),
    ano: ano > 0 ? String(ano) : '',
    assuntos: comoLista(doc.subject).slice(0, 8),
    status: '',
    fonte: derivarFonte(iaId, obra),
    rotuloDaFonte: derivarRotuloDaFonte(iaId),
  });
}

/**
 * `_resolveReadSources`: os endereços de **texto puro** que o leitor tenta, na
 * ordem. A página da obra no Open Library fica de fora de propósito: ela é
 * HTML, e ou entraria como um livro vazio "lido com sucesso", ou engasgaria o
 * separador de capítulos.
 *
 * Para o Gutenberg a convenção do nome é firme e continua valendo. Para o
 * Internet Archive ela é um chute que erra: ver `archive.textos`, que pergunta
 * ao acervo em vez de adivinhar. Este aqui segue sendo a rede de baixo, para
 * quando não dá para perguntar.
 */
export function fontesDeTexto(iaId: string, _obra: string): string[] {
  if (iaId.startsWith('pg') && iaId.length > 2) {
    const id = iaId.slice(2);
    return [
      `https://www.gutenberg.org/cache/epub/${id}/pg${id}.txt`,
      `https://www.gutenberg.org/files/${id}/${id}-0.txt`,
    ];
  }
  if (iaId) {
    return [
      `https://archive.org/download/${iaId}/${iaId}_djvu.txt`,
      `https://archive.org/download/${iaId}/${iaId}.txt`,
    ];
  }
  return [];
}

// ── A estante que sobrevive ao fechar o app ─────────────────────────────
//
// O Open Library leva uns três segundos por pergunta, e a home faz nove de uma
// vez: a estante passava esse tempo inteiro em esqueleto, toda vez que se
// entrava nela. É o acervo, não a tela — medidos, `sort=trending`, sem `sort`
// nenhum e o endpoint `/subjects` dão os mesmos três segundos, então não há
// consulta melhor a escrever. O que dá para não fazer é perguntar de novo.
//
// Serve o que está guardado na hora e, se estiver velho, pergunta por trás: a
// estante aparece agora, com o que se sabia, e se corrige sozinha. Guarda-se o
// `Livro` já reduzido (título, autor, ano, capa), que é um punhado de bytes —
// a resposta crua do acervo é grande demais para caber no guardado.
//
// Uma chave só, com todos os assuntos dentro, porque o celular lê o guardado
// por lista fixa de chaves (`CHAVES`, em `packages/mobile/src/nucleo/api.ts`):
// uma chave por assunto seria uma linha nova lá para cada assunto novo aqui.

const CHAVE_ESTANTE = 'livros_estante_v1';

/** Meio dia de validade. Passou disso, ainda serve — só pergunta de novo. */
const VALIDADE = 12 * 60 * 60 * 1000;

/**
 * Quantos assuntos ficam guardados. A home usa nove; o resto da folga é para
 * os "parecidos" da tela de detalhe, que perguntam por assunto qualquer. Sem
 * teto, um serão de navegação encheria o guardado de assunto visto uma vez.
 */
const TETO_DA_ESTANTE = 14;

interface PrateleiraGuardada {
  em: number;
  livros: Livro[];
}

function lerEstante(): Record<string, PrateleiraGuardada> {
  try {
    const cru = ligacao().guardado.ler(CHAVE_ESTANTE);
    if (!cru) return {};
    const lido: unknown = JSON.parse(cru);
    return lido && typeof lido === 'object' ? (lido as Record<string, PrateleiraGuardada>) : {};
  } catch {
    /* Guardado estragado, ou `ligar()` ainda não chamado (o servidor importa
       este módulo para o proxy de texto). A estante volta a ser só lenta. */
    return {};
  }
}

function gravarPrateleira(chave: string, livros: Livro[]): void {
  try {
    const estante = lerEstante();
    estante[chave] = { em: Date.now(), livros };
    const chaves = Object.keys(estante);
    if (chaves.length > TETO_DA_ESTANTE) {
      const velhas = chaves
        .sort((a, b) => (estante[a]?.em ?? 0) - (estante[b]?.em ?? 0))
        .slice(0, chaves.length - TETO_DA_ESTANTE);
      for (const v of velhas) delete estante[v];
    }
    ligacao().guardado.gravar(CHAVE_ESTANTE, JSON.stringify(estante));
  } catch {
    /* Sem espaço ou sem guardado: perder o atalho não pode derrubar a busca. */
  }
}

export const openLibrary = {
  /** `searchBooks`: até 20, e só o que tem título e alguma identidade. */
  async buscar(termo: string): Promise<Livro[]> {
    if (!termo) return [];
    const dados = await buscarJson<{ docs?: DocOL[] }>(
      `${BUSCA_OL}?q=${encodeURIComponent(termo)}&limit=20`,
    );
    const docs = dados?.docs ?? [];
    return docs
      .filter((d) => !!texto(d.title) && (!!texto(d.key) || comoLista(d.ia).length > 0))
      .map(livroDeDocOL);
  },

  /** `fetchTrending`. Não existe endpoint de "em alta"; o assunto é `best`. */
  emAlta(): Promise<Livro[]> {
    return openLibrary.porAssunto('best', 15);
  },

  /** `discoverBySubject` / `_subjectSearch`, servido da estante quando dá. */
  async porAssunto(assunto: string, limite = 12): Promise<Livro[]> {
    // O limite entra na chave: a home pede doze de "Fantasy" e os "parecidos"
    // da tela de detalhe pedem dez do mesmo assunto. Guardados juntos, um
    // devolveria a lista do outro, no tamanho errado.
    const chave = `${assunto}|${limite}`;

    const perguntar = async (): Promise<Livro[]> => {
      const dados = await buscarJson<{ docs?: DocOL[] }>(
        `${BUSCA_OL}?subject=${encodeURIComponent(assunto)}&limit=${limite}&sort=trending`,
      );
      const livros = (dados?.docs ?? []).filter((d) => !!texto(d.title)).map(livroDeDocOL);
      // Resposta vazia não vira prateleira: acervo fora do ar apagaria a
      // estante boa que já estava guardada.
      if (livros.length > 0) gravarPrateleira(chave, livros);
      return livros;
    };

    const guardada = lerEstante()[chave];
    if (!guardada || !Array.isArray(guardada.livros) || guardada.livros.length === 0) {
      return perguntar();
    }
    if (Date.now() - guardada.em > VALIDADE) void perguntar();
    return guardada.livros;
  },

  /** `fetchWorkDetails`: `/works/OL45804W` → o JSON da obra. */
  async obra(chave: string): Promise<Record<string, unknown> | null> {
    if (!chave) return null;
    return buscarJson<Record<string, unknown>>(`https://openlibrary.org${chave}.json`);
  },

  /** `fetchEditions`: para achar a edição com número de páginas de verdade. */
  async edicoes(chave: string): Promise<Record<string, unknown>[]> {
    if (!chave) return [];
    const dados = await buscarJson<{ entries?: Record<string, unknown>[] }>(
      `https://openlibrary.org${chave}/editions.json?limit=10`,
    );
    return dados?.entries ?? [];
  },

  /**
   * Todas as cópias da obra no Internet Archive, na ordem em que o Open
   * Library as lista.
   *
   * A busca é que traz esse campo, e não o `editions.json`: uma entrada de
   * edição tem `works`, `title`, `publishers`, `isbn_13` e mais um punhado de
   * chaves, e `ia` não é nenhuma delas. Procurar cópia por edição era procurar
   * num lugar onde ela nunca esteve.
   *
   * `key:"…"` devolve exatamente a obra pedida, então uma chamada só basta.
   */
  async copiasNoArchive(chave: string): Promise<string[]> {
    if (!chave) return [];
    const p = new URLSearchParams({ q: `key:"${chave}"`, fields: 'ia', limit: '1' });
    const dados = await buscarJson<{ docs?: { ia?: unknown }[] }>(`${BUSCA_OL}?${p}`);
    return comoLista(dados?.docs?.[0]?.ia).filter((id) => !!id);
  },
};

// ────────────────────────────────────────────────────────────── Gutendex ──

// Com a barra no fim, e ela não é enfeite: o Gutendex responde 301 para
// `/books` e para `/books/<id>`, mandando para a versão barrada. O navegador
// segue o desvio sozinho, então o pedido funcionava — só custava uma ida e
// volta a mais, uns quatro segundos, num acervo que já é o mais lento dos três
// e vivia estourando o prazo de quinze.
const BASE_GUTENDEX = 'https://gutendex.com/books/';

/** `GutenbergService._languageName`. */
function idiomaGutendex(codigo: string): string {
  const nomes: Record<string, string> = {
    en: 'Inglês',
    es: 'Espanhol',
    fr: 'Francês',
    de: 'Alemão',
    zh: 'Chinês',
    ja: 'Japonês',
    ru: 'Russo',
    it: 'Italiano',
    pt: 'Português',
    nl: 'Holandês',
    pl: 'Polonês',
    sv: 'Sueco',
    ar: 'Árabe',
    tr: 'Turco',
  };
  return nomes[codigo.toLowerCase()] ?? codigo.toUpperCase();
}

/**
 * `_firstFormat`: o Gutendex também expõe chaves como
 * `text/plain; charset=utf-8`, então quando a chave exata falha vale o começo.
 */
function primeiroFormato(formatos: Record<string, unknown>, chave: string): string {
  const exato = formatos[chave];
  if (typeof exato === 'string' && exato) return exato;
  for (const [k, v] of Object.entries(formatos)) {
    if (k.startsWith(chave) && typeof v === 'string' && v) return v;
  }
  return '';
}

/**
 * O Gutenberg também guarda livros narrados, e eles vêm na mesma busca.
 *
 * "The Metamorphosis" (26298) é um deles: `media_type` é `Sound`, os formatos
 * são `.ogg`, `.m4b` e `.mp3`, e o único `text/plain` do catálogo é o
 * `26298-readme.txt` — dois quilobytes de aviso sobre os arquivos de som. Esse
 * registro chegava na aba de ler como se fosse o romance, virava o primeiro
 * resultado da busca por "the metamorphosis" e abria o leitor em nada: o aviso
 * no lugar do livro antes, o 404 do `cache/epub` depois. Áudio de livro é a
 * aba "Ouvir", que é do Archive; aqui ele só ocupa a vaga do texto.
 */
function ehParaLer(livro: Record<string, unknown>): boolean {
  return texto(livro.media_type) === 'Text';
}

/** `_mapBook`. */
function resultadoDeGutendex(livro: Record<string, unknown>): ResultadoDeLivro {
  const id = inteiro(livro.id);
  const autores = Array.isArray(livro.authors) ? livro.authors : [];
  const primeiroAutor = autores.length > 0 ? (autores[0] as Record<string, unknown>) : null;
  const formatos =
    livro.formats && typeof livro.formats === 'object'
      ? (livro.formats as Record<string, unknown>)
      : {};
  const idiomas = comoLista(livro.languages);
  const baixados = inteiro(livro.download_count);

  const txt =
    primeiroFormato(formatos, 'text/plain; charset=us-ascii') ||
    primeiroFormato(formatos, 'text/plain') ||
    (id > 0 ? `https://www.gutenberg.org/cache/epub/${id}/pg${id}.txt` : '');
  const epub = primeiroFormato(formatos, 'application/epub+zip');
  const mobi = primeiroFormato(formatos, 'application/x-mobipocket-ebook');
  const html =
    primeiroFormato(formatos, 'text/html') || primeiroFormato(formatos, 'text/html; charset=utf-8');
  const pdf = primeiroFormato(formatos, 'application/pdf');

  const downloads: Record<string, string> = {};
  if (txt) downloads.txt = txt;
  if (epub) downloads.epub = epub;
  if (mobi) downloads.mobi = mobi;
  if (html) downloads.html = html;
  if (pdf) downloads.pdf = pdf;

  return resultadoNovo({
    titulo: texto(livro.title).trim(),
    autor: primeiroAutor ? texto(primeiroAutor.name) : '',
    capa: primeiroFormato(formatos, 'image/jpeg'),
    idioma: idiomas.length > 0 ? idiomaGutendex(idiomas[0]) : '',
    formato: epub ? 'epub' : 'txt',
    votos: baixados > 0 ? baixados : null,
    assuntos: comoLista(livro.subjects)
      .map((s) => s.replace(/\s*--\s*/g, ' · '))
      .filter((s) => s.trim())
      .slice(0, 8),
    acervo: 'Project Gutenberg',
    gutenbergId: id,
    downloads,
    // Aqui o endereço já é real, então os formatos são as chaves dele.
    formatos: Object.keys(downloads),
    candidatos: txt ? [txt] : [],
  });
}

export const gutenberg = {
  /**
   * O `prazo` existe porque o `search=` do Gutendex tem dia ruim: costuma
   * responder em menos de meio segundo, como os outros acervos, e às vezes
   * leva de 11 a 22 segundos — medidos, na mesma busca que antes tinha sido
   * rápida. Com o prazo padrão de 15 segundos a busca boa era abortada no meio
   * e chegava na tela como "não achei". Quem pode esperar passa o seu prazo; a
   * vitrine, que não pode, fica com o padrão.
   */
  async buscar(
    termo: string,
    idioma?: string | null,
    limite = 20,
    prazo?: number,
  ): Promise<ResultadoDeLivro[]> {
    if (!termo.trim()) return [];
    const p = new URLSearchParams({ search: termo.trim() });
    if (idioma) p.set('languages', idioma);
    const dados = await buscarJson<{ results?: Record<string, unknown>[] }>(
      `${BASE_GUTENDEX}?${p}`,
      prazo,
    );
    return (dados?.results ?? [])
      .filter(ehParaLer)
      .slice(0, limite)
      .map(resultadoDeGutendex)
      .filter((b) => !!b.titulo);
  },

  /** Os mais baixados: o análogo legal do "mais populares". */
  async populares(limite = 20): Promise<ResultadoDeLivro[]> {
    const dados = await buscarJson<{ results?: Record<string, unknown>[] }>(
      `${BASE_GUTENDEX}?sort=popular`,
    );
    return (dados?.results ?? [])
      .filter(ehParaLer)
      .slice(0, limite)
      .map(resultadoDeGutendex)
      .filter((b) => !!b.titulo);
  },

  async livro(id: number): Promise<ResultadoDeLivro | null> {
    const dados = await buscarJson<Record<string, unknown>>(`${BASE_GUTENDEX}${id}/`);
    if (!dados) return null;
    const mapeado = resultadoDeGutendex(dados);
    return mapeado.titulo ? mapeado : null;
  },
};

// ────────────────────────────────────────────────────── Internet Archive ──

/* Exportados porque o acervo de música (`musica/acervo.ts`) fala com o mesmo
   Internet Archive: são os mesmos quatro endereços, e ter uma segunda cópia
   deles é ter dois lugares para corrigir quando o acervo mudar de casa. */
export const BUSCA_IA = 'https://archive.org/advancedsearch.php';
export const BAIXAR_IA = 'https://archive.org/download';
export const META_IA = 'https://archive.org/metadata';
export const IMG_IA = 'https://archive.org/services/img';

/* O que o acervo trata como operador na consulta. Tudo isto vira espaço: numa
   caixa de busca de livro ninguém está escrevendo linguagem de consulta, e o
   que chega escrito assim é acidente. */
const OPERADOR_IA = /[+\-!(){}[\]^"~*?:\\/&|]+/g;

/**
 * O termo, na forma que a busca do acervo entende.
 *
 * Dois defeitos moram no mesmo lugar, e os dois foram medidos contra o acervo
 * de verdade.
 *
 * O primeiro é de pontaria. `(termo)` solto procura no documento inteiro —
 * descrição e texto varrido junto — então `o cortiço` achava 249 itens e o
 * primeiro era `Ficção E Confissão`, de Antônio Cândido, que só *fala* do
 * romance: 4133 downloads contra os 596 do livro em si, e a ordenação por
 * downloads faz o resto. Preso a `title` e `creator`, o mesmo termo acha 4, e
 * os 4 são o livro. Trocar a ordenação não era saída: por relevância o acervo
 * devolve documento da CIA para `o cortiço`, então o `downloads desc` fica e a
 * pontaria vem da qualificação.
 *
 * O segundo é de sintaxe. O termo ia cru para dentro da consulta, e parêntese
 * solto, barra ou um `AND` sobrando não devolvem zero: devolvem erro de
 * análise, e a aba ficava vazia sem dizer por quê (medido com `de fé)`,
 * `a/b teste` e `paz AND`). Sem operador nenhum a consulta não tem como
 * quebrar.
 *
 * Devolve vazio quando não sobrou termo — quem chama decide se isso é uma
 * busca sem resultado ou uma vitrine sem filtro.
 */
function alvoDoTermo(termo: string): string {
  const limpo = termo
    .replace(OPERADOR_IA, ' ')
    // Só a forma maiúscula é operador para o acervo; minúscula é palavra.
    .replace(/\b(?:AND|OR|NOT)\b/g, (p) => p.toLowerCase())
    .replace(/\s+/g, ' ')
    .trim();
  return limpo ? `(title:(${limpo}) OR creator:(${limpo}))` : '';
}

/** O que o `archive.textos` descobriu sobre um item. */
export interface TextosDoItem {
  /** Os endereços de texto puro do item, na ordem de preferência. */
  textos: string[];
  /** Item de empréstimo: o texto está lá, mas responde 401 sem o empréstimo. */
  emprestado: boolean;
}

/** `InternetArchiveService._languageName`: aqui o código vem de dois jeitos. */
function idiomaArchive(codigo: string): string {
  const nomes: Record<string, string> = {
    en: 'Inglês',
    eng: 'Inglês',
    es: 'Espanhol',
    spa: 'Espanhol',
    fr: 'Francês',
    fre: 'Francês',
    de: 'Alemão',
    ger: 'Alemão',
    zh: 'Chinês',
    chi: 'Chinês',
    ja: 'Japonês',
    jpn: 'Japonês',
    ru: 'Russo',
    rus: 'Russo',
    it: 'Italiano',
    ita: 'Italiano',
    pt: 'Português',
    por: 'Português',
    nl: 'Holandês',
    dut: 'Holandês',
    pl: 'Polonês',
    pol: 'Polonês',
    ar: 'Árabe',
    ara: 'Árabe',
    tr: 'Turco',
    tur: 'Turco',
  };
  return nomes[codigo.toLowerCase()] ?? codigo.toUpperCase();
}

function extensaoDe(nome: string): string {
  const ponto = nome.lastIndexOf('.');
  if (ponto < 0 || ponto === nome.length - 1) return '';
  return nome.slice(ponto + 1);
}

/** `_itemSizeMb`. */
function tamanhoEmMb(bruto: unknown): number | null {
  const bytes = typeof bruto === 'number' ? bruto : Number(bruto);
  if (!Number.isFinite(bytes) || bytes <= 0) return null;
  return bytes / (1024 * 1024);
}

interface DocIA {
  identifier?: string;
  title?: string;
  creator?: unknown;
  year?: unknown;
  date?: unknown;
  language?: unknown;
  downloads?: unknown;
  item_size?: unknown;
  runtime?: unknown;
  format?: unknown;
  'access-restricted-item'?: unknown;
}

/**
 * Os nomes de formato do acervo → a extensão que a tela mostra.
 *
 * O acervo não diz "pdf": diz "Text PDF", "Image Container PDF", "Additional
 * Text PDF", conforme o derivado. Só interessam os que se leem ou se baixam;
 * "Abbyy GZ", "Djvu XML", "Scandata" e os outros vinte não entram.
 */
const EXTENSAO_POR_FORMATO: Record<string, string> = {
  epub: 'epub',
  'text pdf': 'pdf',
  'image container pdf': 'pdf',
  'additional text pdf': 'pdf',
  pdf: 'pdf',
  djvutxt: 'txt',
  text: 'txt',
  'djvu text': 'txt',
  mobi: 'mobi',
  azw3: 'azw3',
  html: 'html',
  fb2: 'fb2',
};

/**
 * Quais extensões o item tem de verdade, segundo o próprio acervo.
 *
 * Isto substitui a adivinhação por convenção, e a troca foi medida: dos itens
 * livres que a busca traz, só **44%** têm um arquivo chamado `<id>.epub`,
 * `<id>.pdf` ou `<id>_djvu.txt`. Os outros 56% têm o livro, com epub e tudo,
 * sob outro nome — `in.ernet.dli.2015.149403` guarda o seu como
 * `2015.149403.Dom-Casmurro.epub`. O endereço adivinhado dava 404 num livro
 * que existe e é livre, e é isso que fazia "a página do livro não abrir".
 */
function formatosDeDocIA(bruto: unknown): string[] {
  const vistos: string[] = [];
  for (const nome of comoLista(bruto)) {
    const ext = EXTENSAO_POR_FORMATO[nome.trim().toLowerCase()];
    if (ext && !vistos.includes(ext)) vistos.push(ext);
  }
  return vistos;
}

/**
 * `_mapDoc`.
 *
 * Divergência do Flutter, de propósito, e a maior desta tela. Lá o
 * `internet_archive_service.dart:153` monta `<id>.epub`, `<id>.pdf` e
 * `<id>_djvu.txt` para **todo** resultado, sem perguntar nada a ninguém, e o
 * `book_detail_screen.dart:536` remonta os mesmos dois no detalhe. Medido, o
 * palpite erra dos dois lados:
 *
 * - item de empréstimo: os três nomes existem em **100%** deles, então o botão
 *   sempre aparecia — e o arquivo responde **401**, porque o livro está preso;
 * - item livre: só **44%** têm algum dos três nomes. Nos outros 56% o livro tem
 *   epub, mas com outro nome, e o endereço adivinhado responde **404**.
 *
 * Ou seja: o botão de baixar mentia em todo item preso e em mais da metade dos
 * livres. Aqui o resultado nasce sem endereço nenhum e carrega só o que o
 * acervo afirma — quais formatos existem, e se o item é de empréstimo. O nome
 * do arquivo vem depois, do `detalhes`, que é quem lê a lista de verdade.
 */
function resultadoDeDocIA(doc: DocIA): ResultadoDeLivro {
  const id = texto(doc.identifier);
  const criadores = comoLista(doc.creator);
  const idiomas = comoLista(doc.language).map(idiomaArchive);
  const baixados = inteiro(doc.downloads);
  const ano = doc.year ?? doc.date;
  const emprestado = String(doc['access-restricted-item'] ?? '') === 'true';
  const formatos = emprestado ? [] : formatosDeDocIA(doc.format);

  return resultadoNovo({
    titulo: texto(doc.title).trim(),
    autor: criadores.length > 0 ? criadores[0] : '',
    capa: `${IMG_IA}/${id}`,
    ano: ano == null ? '' : String(ano),
    idioma: idiomas.length > 0 ? idiomas[0] : '',
    formato: formatos[0] ?? '',
    tamanhoMb: tamanhoEmMb(doc.item_size),
    votos: baixados > 0 ? baixados : null,
    acervo: 'Internet Archive',
    iaId: id,
    formatos,
    emprestado,
    /* Os candidatos ficam: são o palpite do **leitor**, não o do botão de
       baixar, e ali o palpite tem para onde falhar — o `resolverLeitura`
       pergunta a lista de textos ao acervo antes, e só cai neles quando a rede
       não respondeu. Item preso não ganha nenhum, que é o que faz a tela
       oferecer "Pegar emprestado" em vez de um texto que dá 401. */
    candidatos: emprestado
      ? []
      : [`${BAIXAR_IA}/${id}/${id}_djvu.txt`, `${BAIXAR_IA}/${id}/${id}.txt`],
  });
}

/**
 * Em qual derivado do item o tocador toca, na ordem de preferência.
 *
 * O mesmo capítulo existe em três qualidades no acervo. Escolher um formato e
 * ficar só nele é o que evita a lista sair com o livro inteiro repetido três
 * vezes.
 */
const FORMATOS_DE_AUDIO = ['VBR MP3', '128Kbps MP3', '64Kbps MP3'];

/**
 * A duração como o acervo escreve: "1191.05" em segundos num arquivo,
 * "8:28:54" no runtime do item. As duas formas aparecem, então as duas entram
 * aqui. O resultado é em milissegundos.
 */
export function duracaoDeTexto(bruto: unknown): number {
  const t = String(bruto ?? '').trim();
  if (!t) return 0;
  if (!t.includes(':')) {
    const segundos = Number(t);
    if (!Number.isFinite(segundos) || segundos <= 0) return 0;
    return Math.round(segundos * 1000);
  }
  let total = 0;
  for (const parte of t.split(':')) {
    const n = parseInt(parte.trim(), 10);
    total = total * 60 + (Number.isFinite(n) ? n : 0);
  }
  return total > 0 ? total * 1000 : 0;
}

/**
 * A descrição do LibriVox vem em HTML. Isto tira as tags sem trazer um leitor
 * de HTML junto: são só `<br>`, `<div>` e `<a>`.
 *
 * A sinopse do AniList (ver `anime/catalogo.ts`) chega do mesmo jeito e passa
 * por aqui também — daí o `&#039;` e o `&apos;`, que são como o AniList escreve
 * o apóstrofo e o LibriVox não usa.
 */
export function semHtml(bruto: string): string {
  return bruto
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div)>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&quot;/g, '"')
    .replace(/&#0?39;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * Quem narrou, quando a descrição diz. O LibriVox escreve sempre igual:
 * "Read in Portuguese by Leni; Rachel Moraes; Mayah".
 */
export function narradoresDe(descricao: string): string {
  const achado = /Read (?:in [A-Za-zÀ-ÿ ]+ )?by ([^\n]+)/i.exec(descricao);
  if (!achado) return '';
  return achado[1]
    .replace(/\s*;\s*/g, ', ')
    .replace(/[.,\s]+$/, '')
    .trim();
}

/** `faixasDeArquivos`: as faixas tocáveis de um item, em ordem de capítulo. */
export function faixasDeArquivos(iaId: string, arquivos: unknown[]): Faixa[] {
  const lista = arquivos.filter((a): a is Record<string, unknown> => !!a && typeof a === 'object');
  const formato = FORMATOS_DE_AUDIO.find((f) => lista.some((a) => a.format === f)) ?? '';
  if (!formato) return [];
  const faixas: Faixa[] = [];
  for (const a of lista) {
    if (a.format !== formato) continue;
    const nome = String(a.name ?? '').trim();
    if (!nome) continue;
    const titulo = String(a.title ?? '').trim();
    // O `track` às vezes vem como "3/30"; o que importa é o número da frente.
    const bruto = /^\d+/.exec(String(a.track ?? ''));
    const numero = bruto ? parseInt(bruto[0], 10) : NaN;
    faixas.push({
      numero: Number.isFinite(numero) ? numero : faixas.length + 1,
      titulo: titulo || nome,
      url: `${BAIXAR_IA}/${iaId}/${encodeURIComponent(nome)}`,
      duracaoMs: duracaoDeTexto(a.length),
    });
  }
  faixas.sort((a, b) => a.numero - b.numero);
  return faixas;
}

export const archive = {
  async buscar(termo: string, idioma?: string | null, limite = 20): Promise<ResultadoDeLivro[]> {
    const alvo = alvoDoTermo(termo);
    if (!alvo) return [];
    const clausula = idioma ? ` AND language:(${idioma})` : '';
    const p = new URLSearchParams({
      q: `${alvo} AND mediatype:texts${clausula}`,
      /* `format` e `access-restricted-item` saem de graça no mesmo pedido, e
         são o que dispensa o palpite: um diz quais formatos o item tem, o
         outro diz se ele está preso atrás de um empréstimo. */
      'fl[]':
        'identifier,title,creator,year,downloads,language,item_size,mediatype,' +
        'format,access-restricted-item',
      'sort[]': 'downloads desc',
      rows: String(limite),
      page: '1',
      output: 'json',
    });
    const dados = await buscarJson<{ response?: { docs?: DocIA[] } }>(`${BUSCA_IA}?${p}`);
    return (dados?.response?.docs ?? [])
      .map(resultadoDeDocIA)
      .filter((r) => !!r.titulo && !!r.iaId);
  },

  /** `fetchMetadata`: o que o detalhe precisa e a busca não traz. */
  async detalhes(r: ResultadoDeLivro): Promise<ResultadoDeLivro> {
    if (!r.iaId) return r;
    const dados = await buscarJson<{ metadata?: Record<string, unknown>; files?: unknown[] }>(
      `${META_IA}/${r.iaId}`,
    );
    if (!dados) return r;
    const meta = dados.metadata ?? {};
    const arquivos = dados.files ?? [];

    const descricao = descricaoDe(meta.description);
    const editora = primeiroTexto(meta.publisher);
    const assuntos = comoLista(meta.subject).slice(0, 10);
    const idiomas = comoLista(meta.language).map(idiomaArchive);
    const anoBruto = meta.year ?? meta.date;
    const ano = anoBruto == null ? r.ano : String(anoBruto);

    /* Item de empréstimo não tem download nenhum. Os arquivos aparecem na
       lista, com tamanho e tudo, e respondem 401 para quem não pegou o livro:
       o único que responde 200 é o `_encrypted.pdf`, que vem com DRM e que o
       leitor não abre. Por isso o laço nem roda — varrer a lista aqui só
       serviria para montar endereço que não baixa. */
    const emprestado = String(meta['access-restricted-item'] ?? '') === 'true';

    const downloads: Record<string, string> = {};
    if (!emprestado) {
      for (const bruto of arquivos) {
        if (!bruto || typeof bruto !== 'object') continue;
        const arquivo = bruto as Record<string, unknown>;
        const nome = texto(arquivo.name);
        const ext = extensaoDe(nome).toLowerCase();
        // O `_djvu` fica de fora: é o texto que só existe durante um empréstimo.
        if (!ext || nome.includes('_djvu')) continue;
        if (['txt', 'epub', 'pdf', 'mobi', 'azw3', 'html', 'fb2'].includes(ext)) {
          if (!(ext in downloads)) downloads[ext] = `${BAIXAR_IA}/${r.iaId}/${nome}`;
        }
      }
    }

    const achados = Object.keys(downloads);
    const principal =
      achados.find((e) => e === 'epub') ??
      achados.find((e) => e === 'txt') ??
      achados.find((e) => e === 'pdf') ??
      achados[0] ??
      /* Resultado da Open Library também passa por aqui quando traz `iaId`, e
         aí o formato que ele já tinha vale mais do que um vazio. */
      r.formato;

    return {
      ...r,
      descricao: descricao || r.descricao,
      editora: editora || r.editora,
      assuntos: assuntos.length > 0 ? assuntos : r.assuntos,
      idioma: idiomas.length > 0 ? idiomas[0] : r.idioma,
      ano: ano || r.ano,
      formato: principal,
      tamanhoMb: tamanhoEmMb(meta.item_size) ?? r.tamanhoMb,
      emprestado,
      /* A ficha é a palavra final: ela leu a lista de arquivos de verdade.
         Quando não achou nada, não achou mesmo — antes isto caía de volta no
         `r.downloads` adivinhado, que era justamente o que dava 404. */
      formatos: achados,
      downloads,
    };
  },

  /**
   * Os endereços de texto que o item tem de verdade, perguntados ao acervo em
   * vez de adivinhados.
   *
   * O `fontesDeTexto` monta `{id}/{id}_djvu.txt` por convenção, e a convenção
   * erra: no `in.ernet.dli.2015.184960` o derivado se chama
   * `2015.184960.The-Great-Gatsby_djvu.txt`, e o endereço adivinhado devolve
   * 404 com 137 KB de página de erro. O `/metadata/{id}` diz o nome certo, é
   * aberto a CORS e responde a GET (o HEAD dele volta 405).
   *
   * Lista vazia é resposta, e não falha: nos três casos abaixo não existe
   * arquivo para ler, e insistir só gastaria o prazo do leitor.
   *
   * - `access-restricted-item`: o `_djvu.txt` aparece na lista, com tamanho e
   *   tudo, e responde **401** para quem não pegou o livro emprestado. Este é
   *   o único que sai marcado como `emprestado`, porque é o único em que a
   *   janela do acervo abre uma prévia em vez do livro.
   * - `mediatype` que não é `texts`: o disco de vinil de Moby Dick também tem
   *   `_djvu.txt`, e o que está escrito nele é a capa do disco.
   * - item sem derivado `DjVuTXT`: sobra o `_origin.txt` de 43 bytes.
   *
   * `null` é outra coisa: o acervo não respondeu. Quem chama volta para o
   * endereço adivinhado em vez de dar o livro por ilegível.
   */
  async textos(iaId: string): Promise<TextosDoItem | null> {
    if (!iaId) return { textos: [], emprestado: false };
    const dados = await buscarJson<{
      metadata?: Record<string, unknown>;
      files?: { name?: unknown; format?: unknown }[];
    }>(`${META_IA}/${iaId}`);
    if (!dados) return null;

    const meta = dados.metadata ?? {};
    const emprestado = String(meta['access-restricted-item'] ?? '') === 'true';
    if (emprestado) return { textos: [], emprestado: true };
    if (texto(meta.mediatype) !== 'texts') return { textos: [], emprestado: false };

    const nomes = (dados.files ?? [])
      .filter((a) => texto(a?.format) === 'DjVuTXT')
      .map((a) => texto(a?.name))
      .filter((n) => !!n);
    // O derivado que repete o nome do item primeiro: quando existe, é o do
    // livro. `sort` é estável, então o resto mantém a ordem do acervo.
    const doItem = `${iaId}_djvu.txt`;
    nomes.sort((a, b) => Number(b === doItem) - Number(a === doItem));
    // Nome de arquivo entra num caminho, e há item com barra no nome: cada
    // pedaço é escapado por si para a barra continuar sendo barra.
    return {
      textos: nomes.map(
        (n) => `${BAIXAR_IA}/${iaId}/${n.split('/').map(encodeURIComponent).join('/')}`,
      ),
      emprestado: false,
    };
  },

  /**
   * Audiolivros narrados por gente, do acervo do LibriVox. Sem termo a busca
   * não fica vazia: vira a lista dos mais ouvidos, que é o que a aba mostra
   * quando alguém acabou de abrir e ainda não digitou nada.
   *
   * O `idioma` é o código de três letras do acervo (`por`, `eng`, `spa`), como
   * no `buscar` aqui do lado — o campo `language` não guarda nome por extenso,
   * e pedir por nome acha zero.
   */
  async buscarAudiolivros(
    termo: string,
    idioma?: string | null,
    limite = 24,
  ): Promise<Audiolivro[]> {
    const alvo = alvoDoTermo(termo);
    const clausula = idioma ? ` AND language:(${idioma})` : '';
    const p = new URLSearchParams({
      q: `${alvo ? `${alvo} AND ` : ''}collection:librivoxaudio${clausula}`,
      'fl[]': 'identifier,title,creator,year,downloads,language,runtime',
      'sort[]': 'downloads desc',
      rows: String(limite),
      page: '1',
      output: 'json',
    });
    const dados = await buscarJson<{ response?: { docs?: DocIA[] } }>(`${BUSCA_IA}?${p}`);
    return (dados?.response?.docs ?? [])
      .map((doc) => {
        const id = texto(doc.identifier);
        const criadores = comoLista(doc.creator);
        const idiomas = comoLista(doc.language).map(idiomaArchive);
        return audiolivroNovo({
          iaId: id,
          titulo: texto(doc.title).trim(),
          autor: criadores.length > 0 ? criadores[0] : '',
          capa: id ? `${IMG_IA}/${id}` : '',
          idioma: idiomas.length > 0 ? idiomas[0] : '',
          ano: String(doc.year ?? '').trim(),
          duracaoMs: duracaoDeTexto(doc.runtime),
          ouvidos: inteiro(doc.downloads),
        });
      })
      .filter((a) => !!a.iaId && !!a.titulo);
  },

  /**
   * As faixas do audiolivro, que a busca não traz. Devolve o próprio livro sem
   * faixas quando o acervo não responde: a tela mostra o aviso de que não deu,
   * em vez de uma lista vazia sem explicação.
   */
  async faixas(livro: Audiolivro): Promise<Audiolivro> {
    if (!livro.iaId) return livro;
    const dados = await buscarJson<{ metadata?: Record<string, unknown>; files?: unknown[] }>(
      `${META_IA}/${livro.iaId}`,
    );
    if (!dados) return livro;
    const faixas = faixasDeArquivos(livro.iaId, dados.files ?? []);
    if (faixas.length === 0) return livro;
    const descricao = descricaoDe((dados.metadata ?? {}).description);
    const total = faixas.reduce((soma, f) => soma + f.duracaoMs, 0);
    return {
      ...livro,
      faixas,
      duracaoMs: total > 0 ? total : livro.duracaoMs,
      descricao,
      narradores: narradoresDe(descricao),
    };
  },
};
